import {appAPI} from "../../../api";
import {
    IDispatchJob,
    IDispatchPoint,
    IDispatchRoute,
    IJobValidators,
    IShareLink,
    IBalancePair,
    IBalanceResult,
} from "../../../models/IDispatch";

const pointsPath = `/dispatch/points`;
const jobsPath = `/dispatch/jobs`;
const routesPath = `/dispatch/routes`;

/** numeric-колонки Postgres приходят строками — нормализуем один раз здесь. */
const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const normalizePoint = (p: any): IDispatchPoint => ({
    ...p,
    max_height_m: num(p.max_height_m),
});

const normalizeJob = (j: any): IDispatchJob => ({
    ...j,
    cargo_weight_kg: num(j.cargo_weight_kg),
    cargo_volume_m3: num(j.cargo_volume_m3),
    amount: num(j.amount),
    point_snapshot: j.point_snapshot
        ? {...j.point_snapshot, max_height_m: num(j.point_snapshot.max_height_m)}
        : j.point_snapshot,
});

const normalizeRoute = (r: any): IDispatchRoute => ({
    ...r,
    planned_distance_km: num(r.planned_distance_km),
    jobs: Array.isArray(r.jobs) ? r.jobs.map(normalizeJob) : r.jobs,
});

const throwReadable = (error: any): never => {
    const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        `Ошибка сервера: ${error.response?.status} ${error.response?.statusText}`;
    throw new Error(Array.isArray(message) ? message.join("; ") : message);
};

export const dispatchAPI = {
    // ── Справочник точек ────────────────────────────────────────────────────
    getPoints: async (includeArchived = false): Promise<IDispatchPoint[]> => {
        const res = await appAPI.get(pointsPath, {params: {includeArchived}});
        return res.data.map(normalizePoint);
    },
    addPoint: async (body: Partial<IDispatchPoint>): Promise<IDispatchPoint> => {
        try {
            const res = await appAPI.post(pointsPath, body);
            return normalizePoint(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    updatePoint: async (id: string, body: Partial<IDispatchPoint>): Promise<IDispatchPoint> => {
        try {
            const res = await appAPI.patch(`${pointsPath}/${id}`, body);
            return normalizePoint(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    setPointArchived: async (id: string, archived: boolean): Promise<IDispatchPoint> => {
        const res = await appAPI.patch(`${pointsPath}/${id}/${archived ? "archive" : "unarchive"}`);
        return normalizePoint(res.data);
    },

    // ── Заявки ──────────────────────────────────────────────────────────────
    getJobs: async (date: string): Promise<IDispatchJob[]> => {
        const res = await appAPI.get(jobsPath, {params: {date}});
        return res.data.map(normalizeJob);
    },
    addJob: async (body: Record<string, unknown>): Promise<IDispatchJob> => {
        try {
            const res = await appAPI.post(jobsPath, body);
            return normalizeJob(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    updateJob: async (id: string, body: Record<string, unknown>): Promise<IDispatchJob> => {
        try {
            const res = await appAPI.patch(`${jobsPath}/${id}`, body);
            return normalizeJob(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    skipJob: async (id: string): Promise<IDispatchJob> => {
        const res = await appAPI.post(`${jobsPath}/${id}/skip`);
        return normalizeJob(res.data);
    },
    deleteJob: async (id: string): Promise<void> => {
        try {
            await appAPI.delete(`${jobsPath}/${id}`);
        } catch (e) {
            throwReadable(e);
        }
    },
    closeJob: async (id: string, comment?: string): Promise<IDispatchJob> => {
        try {
            const res = await appAPI.post(`${jobsPath}/${id}/close`, {comment});
            return normalizeJob(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    failJob: async (id: string, reason: string): Promise<IDispatchJob> => {
        try {
            const res = await appAPI.post(`${jobsPath}/${id}/fail`, {reason});
            return normalizeJob(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    confirmJob: async (id: string): Promise<IDispatchJob> => {
        const res = await appAPI.post(`${jobsPath}/${id}/confirm`);
        return normalizeJob(res.data);
    },
    rejectJob: async (id: string): Promise<IDispatchJob> => {
        const res = await appAPI.post(`${jobsPath}/${id}/reject`);
        return normalizeJob(res.data);
    },
    getJobValidators: async (id: string): Promise<IJobValidators | null> => {
        const res = await appAPI.get(`${jobsPath}/${id}/validators`);
        return res.data;
    },

    // ── Рейсы ───────────────────────────────────────────────────────────────
    getRoutes: async (date: string): Promise<IDispatchRoute[]> => {
        const res = await appAPI.get(routesPath, {params: {date}});
        return res.data.map(normalizeRoute);
    },
    addRoute: async (body: {
        date: string;
        machinery_id: string;
        driver_id: string;
        job_ids?: string[];
    }): Promise<IDispatchRoute> => {
        try {
            const res = await appAPI.post(routesPath, body);
            return normalizeRoute(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    updateRoute: async (
        id: string,
        body: {machinery_id?: string; driver_id?: string; job_ids?: string[]},
    ): Promise<IDispatchRoute> => {
        try {
            const res = await appAPI.patch(`${routesPath}/${id}`, body);
            return normalizeRoute(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    approveRoute: async (id: string): Promise<IDispatchRoute> => {
        const res = await appAPI.post(`${routesPath}/${id}/approve`);
        return normalizeRoute(res.data);
    },
    unapproveRoute: async (id: string): Promise<IDispatchRoute> => {
        const res = await appAPI.post(`${routesPath}/${id}/unapprove`);
        return normalizeRoute(res.data);
    },
    cancelRoute: async (id: string): Promise<IDispatchRoute> => {
        const res = await appAPI.post(`${routesPath}/${id}/cancel`);
        return normalizeRoute(res.data);
    },
    deleteRoute: async (id: string): Promise<void> => {
        try {
            await appAPI.delete(`${routesPath}/${id}`);
        } catch (e) {
            throwReadable(e);
        }
    },
    // ── Балансировщик ───────────────────────────────────────────────────────
    runBalance: async (body: {
        date: string;
        pairs: IBalancePair[];
        work_from?: string;
        work_to?: string;
        traffic_coef?: number;
    }): Promise<IBalanceResult> => {
        try {
            const res = await appAPI.post(`/dispatch/balance`, body);
            return res.data;
        } catch (e) {
            return throwReadable(e);
        }
    },

    // ── Клиентские ссылки ───────────────────────────────────────────────────
    createShareLink: async (
        jobId: string,
        body: {days?: number; mode?: string; destination_text?: string; destination_lat?: number; destination_lon?: number},
    ): Promise<IShareLink> => {
        try {
            const res = await appAPI.post(`${jobsPath}/${jobId}/share`, body);
            return res.data;
        } catch (e) {
            return throwReadable(e);
        }
    },
    getShareLinks: async (jobId: string): Promise<IShareLink[]> => {
        const res = await appAPI.get(`${jobsPath}/${jobId}/share`);
        return res.data;
    },
    revokeShareLink: async (linkId: string): Promise<void> => {
        await appAPI.delete(`/dispatch/share/${linkId}`);
    },

    // ── Кабинет водителя ────────────────────────────────────────────────────
    /** Мой рейс на день. 200 с пустым телом = «задания ещё не утверждены». */
    getMyRoute: async (date: string): Promise<IDispatchRoute | null> => {
        const res = await appAPI.get(`/dispatch/my/route`, {params: {date}});
        return res.data ? normalizeRoute(res.data) : null;
    },
    getMyRoutes: async (limit = 30, offset = 0): Promise<IDispatchRoute[]> => {
        const res = await appAPI.get(`/dispatch/my/routes`, {params: {limit, offset}});
        return res.data.map(normalizeRoute);
    },
    getMyTrustState: async (date: string): Promise<{trust_state: string}> => {
        const res = await appAPI.get(`/dispatch/my/trust-state`, {params: {date}});
        return res.data;
    },
    /** Перестановка оставшихся точек (право водителя, без согласования). */
    reorderMyRoute: async (date: string, jobIds: string[]): Promise<IDispatchRoute> => {
        try {
            const res = await appAPI.patch(`/dispatch/my/route/order`, {job_ids: jobIds}, {params: {date}});
            return normalizeRoute(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },
    arriveJob: async (id: string): Promise<IDispatchJob> => {
        try {
            const res = await appAPI.post(`${jobsPath}/${id}/arrive`);
            return normalizeJob(res.data);
        } catch (e) {
            return throwReadable(e);
        }
    },

    /** Ручной пересчёт визитов (идемпотентен) — «пересчитать сейчас». */
    reconcileRoute: async (id: string): Promise<IDispatchRoute> => {
        const res = await appAPI.post(`${routesPath}/${id}/reconcile`);
        return normalizeRoute(res.data);
    },
};
